import React from 'react';
import { observer } from 'mobx-react';
import {
  Grid, Typography, IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from '@gamiphy/react-i18n';
import { Popup } from '@gamiphy/loyalty-station-desktop/components/popup';
import { Controller, useForm } from 'react-hook-form';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { GButton } from '@gamiphy/loyalty-station-desktop/uikit';
import CloseIcon from '@mui/icons-material/Close';
import { UserMissionCategoryPopulated } from '@gamiphy/bl/mission';
import { Points } from '@gamiphy/loyalty/points';
import { AnalyticsFactory } from '@gamiphy/analytics';
import { SessionHitTypes } from '@gamiphy/bl/analytics';
import SVG from 'react-inlinesvg';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useGamix } from '@gamiphy/loyalty-station-desktop/context';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MissionTitle } from '@gamiphy/loyalty/missions';
import { LevelStore } from '@gamiphy/loyalty-station-desktop/store/level';

export type Props = {
  title: string,
  open: boolean,
  onClose: () => void,
  userMission?: UserMissionCategoryPopulated
};

type FormValues = {
  birthDate: Date | null,
};

const BirthdayPopup: React.FC<Props> = observer(({
  title, open, onClose, userMission,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { container } = useGamix();
  const defaultValues = React.useMemo(() => {
    const dateOfBirth = UserStore.user?.dateOfBirth;
    const concatDate = dateOfBirth ? new Date(`${dateOfBirth.year}-${dateOfBirth.month}-${dateOfBirth.day}`) : null;
    return { birthDate: concatDate };
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues,
  });

  const values = watch();

  const onSubmit = React.useCallback<React.FormEventHandler<HTMLFormElement>>(handleSubmit(
    async (data: FormValues) => {
      try {
        if (data.birthDate) {
          await UserStore.update({
            dateOfBirth: {
              day: data.birthDate.getUTCDate(),
              month: data.birthDate.getUTCMonth() + 1,
              year: data.birthDate.getUTCFullYear(),
            },
          });
        }
      } finally {
        onClose();
        void AnalyticsFactory.collect({
          type: SessionHitTypes.event,
          event: {
            category: 'BirthdayMission',
            action: 'SaveDate.Clicked',
            label: 'SaveDate_BirthdayMission',
          },
        });
      }
    },
  ), [handleSubmit]);

  const Header = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(2),
    width: '100%',
  }));

  return (
    <Popup fullWidth open={open} onClose={onClose}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <form onSubmit={onSubmit}>
          <Grid container justifyContent="center">
            <Header>
              <IconButton sx={{ p: 0, textAlign: 'right' }} onClick={onClose}>
                <CloseIcon sx={{ color: 'text.secondary', fontSize: '2.5rem' }} />
              </IconButton>
              <Typography sx={{
                fontSize: '1.4rem', textAlign: 'center', color: 'text.secondary', mt: 0.5,
              }}
              >
                {t('birthday.popup.title', 'Complete Missions to level up and unlock better rewards!')}
              </Typography>
            </Header>
            <Grid container sx={{ width: '100%', py: 2, px: 1 }} spacing={2}>
              <Grid>{title}</Grid>
              {userMission && (
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                wrap="nowrap"
                columnSpacing={1}
              >
                <Grid>
                  <SVG
                    src={userMission.mission.image}
                    style={{ width: '30px', height: '30px' }}
                  >
                    <img src={userMission.mission.image} width="30" height="30" alt="" />
                  </SVG>
                </Grid>
                <Grid sx={{ flex: 2 }} container direction="column">
                  <Grid size={12}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontSize: '1.2rem', fontWeight: 500, lineHeight: 1.25, mb: 0.4,
                      }}
                    >
                      <MissionTitle mission={userMission.mission} category={userMission.mission.category} />
                    </Typography>
                  </Grid>
                  <Grid size={12} container>
                    <Points points={userMission.mission.points} tierConfig={LevelStore.tierConfig} />
                  </Grid>
                </Grid>
              </Grid>
              )}
              <Grid>
                <Typography>
                  {t('birthday.mission.desc', 'Update your birth date to receive a reward on your birthday!')}
                </Typography>
              </Grid>
              <Grid sx={{ width: '100%' }}>
                <Controller
                  name="birthDate"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({
                    field: {
                      value,
                      onChange,
                    },
                  }) => (
                    <DatePicker
                      disableFuture
                      openTo="year"
                      views={['year', 'month', 'day']}
                      value={value}
                      onChange={onChange}
                      orientation="portrait"
                      format="dd/MM/yyyy"
                      slotProps={{
                        dialog: {
                          container,
                        },
                        popper: {
                          anchorEl,
                        },
                        textField: {
                          autoComplete: 'off',
                          placeholder: 'dd/MM/yyyy',
                          onKeyDown: (e) => e.preventDefault(),
                          fullWidth: true,
                          onClick: (e) => setAnchorEl(e.currentTarget),
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid sx={{ width: '100%' }}>
                <GButton
                  disabled={!values.birthDate}
                  variant="contained"
                  color="primary"
                  type="submit"
                  sx={{ height: 45 }}
                  loading={isSubmitting}
                  fullWidth
                >
                  {t('birthday.saveDate', 'Save date')}
                </GButton>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </LocalizationProvider>
    </Popup>
  );
});

export default BirthdayPopup;
